/**
 * Reaccionar a un mensaje de WhatsApp.
 *
 * POR QUÉ ES UN HOOK Y NO CÓDIGO SUELTO EN LA PÁGINA. Reaccionar parece un botón y son tres pasos
 * con un orden que importa: pintar la reacción al instante, guardarla en la base, y recién entonces
 * mandarla a WhatsApp. Estaba escrito dentro de la página de Chats, así que cualquier otra pantalla
 * que montara el mismo hilo de mensajes tenía dos opciones: copiarlo o quedarse sin reacciones.
 *
 * 🪤 EL EMOJI VACÍO NO ES UN ERROR: ES QUITAR LA REACCIÓN. WhatsApp usa el mismo mensaje para poner
 * y para sacar; tratar `''` como "no hay nada que mandar" deja al usuario sin poder deshacer.
 *
 * 🪤 SOLO SE MANDA A WHATSAPP SI EL MENSAJE YA EXISTE ALLÁ (`wa_message_id`). Un mensaje todavía en
 * vuelo —optimista, con id temporal— no tiene contra qué reaccionar del otro lado; mandarlo igual
 * devuelve un error que no le sirve a nadie.
 */

import { useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { devLog } from '@/lib/utils';
import type { WaMessage, MessageReaction } from '@/components/whatsapp/WaMessageThread';

interface Options {
  conversationId: string | null;
  messages: WaMessage[];
  setMessages: React.Dispatch<React.SetStateAction<WaMessage[]>>;
}

/** Devuelve el handler que espera `WaMessageThread` en `onSendReaction`. */
export function useWaReactions({ conversationId, messages, setMessages }: Options) {
  return useCallback((msgId: string, emoji: string) => {
    // Optimista: la reacción aparece antes de que la red conteste. Si falla, se revierte abajo.
    setMessages((prev) => prev.map((m) => {
      if (m.id !== msgId) return m;
      const otras = m.reactions.filter((r) => !r.from_me);
      if (emoji) otras.push({ emoji, from: 'agent', from_me: true, timestamp: Math.floor(Date.now() / 1000) });
      return { ...m, reactions: otras };
    }));

    if (!conversationId) return;
    const msg = messages.find((m) => m.id === msgId);
    if (!msg) return;

    const reacciones = (msg.reactions ?? []).filter((r: MessageReaction) => !r.from_me);
    if (emoji) {
      reacciones.push({ emoji, from: 'agent', from_me: true, timestamp: Math.floor(Date.now() / 1000) });
    }

    void supabase
      .from('wa_messages')
      .update({ reactions: reacciones })
      .eq('id', msgId)
      .then(({ error }) => {
        if (error) {
          devLog.error('DB reaction error:', error);
          setMessages((prev) => prev.map((m) => (m.id === msgId
            ? { ...m, reactions: m.reactions.filter((r) => !r.from_me || r.emoji !== emoji) }
            : m)));
          return;
        }

        if (!msg.wa_message_id) return;
        void supabase.functions.invoke('whatsapp-send-message', {
          body: { conversation_id: conversationId, action: 'react', message_id: msgId, emoji },
        }).then(({ error: sendErr }) => {
          // Fire-and-forget a propósito: la reacción ya está guardada de nuestro lado. Si WhatsApp
          // la rechaza, revertir la UI sería peor — el usuario la vería desaparecer sin motivo.
          if (sendErr) devLog.warn('WA reaction send error:', sendErr);
        });
      });
  }, [conversationId, messages, setMessages]);
}
